/// <amd-module name="TFS/Admin/Groups/Common/Util/ErrorMessageHelper" />
import * as Resources from "TFS/Admin/Groups/Common/Resources";
import { GroupsComponent } from "TFS/Admin/Groups/Common/Util/GroupsTelemetryLogger";
import { caseInsensitiveContains, localeFormat } from "VSS/Core/Util/String";
import { ParseErrorFromAPI } from "TFS/Admin/Common/Util/CustomErrorHelper";
import { CustomErrorSearchString } from "TFS/Admin/Common/Constants/CustomErrorConstants";

export enum IErrorAction {
    Add,
    Delete,
    Edit
}

export function getErrorMessage(subFeature: GroupsComponent, error: any, action: IErrorAction): string {
    const errorMessage: string = getDefaultErrorString(subFeature, action);

    let customMessage = "";

    if (error && error.serverError) {
        customMessage = error.serverError.message;

        if (error.serverError.typeKey) {
            switch (error.serverError.typeKey) {
                case IErrorName.AccessCheckException:
                    customMessage = getPermissionsAccessErrorString(subFeature, action);
                    break;
                case IErrorName.RemoveSpecialGroupException:
                    customMessage = getRemoveSpecialGroupErrorString(subFeature, action, error);
                    break;
                case IErrorName.CannotUpdateWellKnownGraphGroupException:
                    customMessage = getEditSpecialGroupErrorString(subFeature, action);
                    break;
                case IErrorName.AddMemberCyclicMembershipException:
                    customMessage = parseErrorMessage(error.message, ErrorMessageSearchString.AcyclicMember);
                    break;
                case IErrorName.ModifyEveryoneGroupException:
                    customMessage = parseErrorMessage(error.message, ErrorMessageSearchString.ModifyEveryone);
                    break;
                case IErrorName.RemoveAccountOwnerFromAdminGroupException:
                    customMessage = error.message;
                    break;
                case IErrorName.MemberNotActiveException:
                    customMessage = Resources.AddMembersFailedDueToInactiveMember;
                    break;
                case IErrorName.AddProjectGroupProjectMismatchException:
                    customMessage = parseErrorMessage(error.message, ErrorMessageSearchString.AddProjectGroupProjectMismatch);
                    break;
                case IErrorName.TeamAlreadyExistsException:
                    customMessage = localeFormat(Resources.TeamAlreadyExistsError, error.message);
                    break;
                case IErrorName.AddProjectGroupToGlobalGroupException:
                    customMessage = parseErrorMessage(error.message, ErrorMessageSearchString.AddProjectGroupToGlobalGroup);
                    break;
                default:
                    break;
            }
        } else {
            if (caseInsensitiveContains(error.serverError, IErrorName.ModifyEveryoneGroupException)) {
                customMessage = parseErrorMessage(error.serverError, ErrorMessageSearchString.ModifyEveryone);
            }
        }
    }
    if (caseInsensitiveContains(ParseErrorFromAPI(error), CustomErrorSearchString.AadAccessSilentException)) {
        customMessage = Resources.AadAccessSilentExceptionMessage;
    }
   
    return !customMessage ? errorMessage : customMessage;
}

function getDefaultErrorString(subFeature: GroupsComponent, action: IErrorAction): string {
    switch (subFeature) {
        case GroupsComponent.GroupMembersPivot:
            if (action === IErrorAction.Delete) {
                return Resources.DeleteMembersFailed;
            } else if (action === IErrorAction.Add) {
                return Resources.AddMembersFailed;
            }
            break;
        case GroupsComponent.MemberOfPivot:
            if (action === IErrorAction.Delete) {
                return Resources.DeleteMemberOfFailed;
            } else if (action === IErrorAction.Add) {
                return Resources.AddMemberOfFailed;
            }
            break;
        case GroupsComponent.GroupsPivot:
            if (action === IErrorAction.Add) {
                return Resources.AddGroupsFailed;
            } else if (action === IErrorAction.Edit) {
                return Resources.GroupEditFail;
            }
            break;
        case GroupsComponent.SettingsPivot:
            if (action === IErrorAction.Delete) {
                return Resources.DeleteGroupsFailed;
            }
        default:
            break;
    }

    return "";
}

function getPermissionsAccessErrorString(subFeature: GroupsComponent, action: IErrorAction): string {
    switch (subFeature) {
        case GroupsComponent.GroupMembersPivot:
            if (action === IErrorAction.Delete) {
                return Resources.DeleteMembersFailedDueToPermissions;
            } else if (action === IErrorAction.Add) {
                return Resources.AddMembersFailedDueToPermissions;
            }
            break;
        case GroupsComponent.MemberOfPivot:
            if (action === IErrorAction.Delete) {
                return Resources.DeleteMemberOfFailedDueToPermissions;
            } else if (action === IErrorAction.Add) {
                return Resources.AddMemberOfFailedDueToPermissions;
            }
            break;
        case GroupsComponent.GroupsPivot:
            if (action === IErrorAction.Delete) {
                return Resources.DeleteGroupsFailedDueToPermissions;
            } else if (action === IErrorAction.Add) {
                return Resources.AddGroupsFailedDueToPermissions;
            } else if (action === IErrorAction.Edit) {
                return Resources.GroupEditFailedDueToPermissions;
            }
            break;
        default:
            break;
    }

    return "";
}

function getRemoveSpecialGroupErrorString(subFeature: GroupsComponent, action: IErrorAction, error: any): string {
    switch (subFeature) {
        case GroupsComponent.GroupsPivot:
            if (action === IErrorAction.Delete) {
                return error.message;
            }
            break;
        default:
            break;
    }

    return "";
}

function getEditSpecialGroupErrorString(subFeature: GroupsComponent, action: IErrorAction): string {
    switch (subFeature) {
        case GroupsComponent.GroupsPivot:
            if (action === IErrorAction.Edit) {
                return Resources.StandardGroupEditFail;
            }
            break;
        default:
            break;
    }

    return "";
}

function parseErrorMessage(errorMessage: string, token: string): string {
    if (caseInsensitiveContains(errorMessage, token)) {
        const splitMessage = errorMessage.split(token);
        const removeStackTrace = splitMessage[splitMessage.length - 1].split("\n");
        return removeStackTrace[0];
    }

    return "";
}

enum IErrorName {
    AccessCheckException = "AccessCheckException",
    CannotUpdateWellKnownGraphGroupException = "CannotUpdateWellKnownGraphGroupException",
    RemoveSpecialGroupException = "RemoveSpecialGroupException",
    AddMemberCyclicMembershipException = "AddMemberCyclicMembershipException",
    ModifyEveryoneGroupException = "ModifyEveryoneGroupException",
    RemoveAccountOwnerFromAdminGroupException = "RemoveAccountOwnerFromAdminGroupException",
    MemberNotActiveException = "MemberNotActiveException",
    TeamAlreadyExistsException = "TeamAlreadyExistsException",
    AddProjectGroupProjectMismatchException = "AddProjectGroupProjectMismatchException",
    AddProjectGroupToGlobalGroupException = "AddProjectGroupToGlobalGroupException"
}

namespace ErrorMessageSearchString {
    export const AcyclicMember = "TF50233:";
    export const ModifyEveryone = "TF50618:";
    export const AddProjectGroupProjectMismatch = "TF50375:";
    export const AddProjectGroupToGlobalGroup = "TF400031:";
}
